import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Flame, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "Last 30 days" },
  { key: "all", label: "All-time" },
];

/**
 * /hall-of-phoenix — public, auto-curated wall of the highest-reacted
 * community posts (50+ reactions). Tabs for week / month / all-time.
 */
export default function HallOfPhoenix() {
  const [period, setPeriod] = useState("week");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minReactions, setMinReactions] = useState(50);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/hall-of-phoenix?period=${period}&limit=30`);
        if (cancelled) return;
        setItems(data?.items || []);
        setMinReactions(data?.min_reactions || 50);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const champion = items[0];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="hall-of-phoenix-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
            Community Wall
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mt-2 flex items-center justify-center gap-3">
            🏛️ Hall of Phoenix
          </h1>
          <p className="text-neutral-400 text-sm mt-3 max-w-prose mx-auto">
            The most-loved moments in the OmniMuscle community. {minReactions}+ reactions to earn a spot.
          </p>
        </div>

        {/* Period tabs */}
        <div className="flex justify-center gap-1 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`hop-tab-${t.key}`}
              onClick={() => setPeriod(t.key)}
              className={
                "uppercase tracking-[0.2em] text-xs font-bold py-2.5 px-4 border-2 transition-colors " +
                (period === t.key
                  ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]"
                  : "bg-transparent text-neutral-400 border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37]")
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="hop-empty"
            className="text-center py-16 border-2 border-dashed border-zinc-800 bg-[#0F0F0F] text-neutral-500"
          >
            <div className="text-5xl mb-3">🌱</div>
            <div className="font-display text-2xl text-white mb-2">No champions yet</div>
            <div className="text-sm">
              A post needs {minReactions}+ reactions to land here. Be the first this {period === "all" ? "time" : period}.
            </div>
          </div>
        ) : (
          <>
            {/* Champion hero card */}
            {champion && (
              <ChampionCard item={champion} data-testid="hop-champion" />
            )}

            {/* The rest */}
            {items.length > 1 && (
              <div className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="hop-list">
                {items.slice(1).map((item, i) => (
                  <PhoenixCard key={item.event_id} item={item} rank={i + 2} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ChampionCard({ item }) {
  return (
    <div
      data-testid="hop-champion"
      className="relative bg-[#0F0F0F] border-2 border-[#D4AF37] p-6 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(212,175,55,0.15) 0%, transparent 60%), #0A0A0A",
      }}
    >
      <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[#D4AF37]">
        <Crown size={14} />
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold">#1 Champion</span>
      </div>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="text-6xl">{item.icon}</div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              {item.subtitle}
            </div>
            <TrendingChip item={item} />
          </div>
          <div className="font-display text-3xl text-white leading-tight mt-1">{item.title}</div>
          <AuthorLine author={item.author} />
          <ReactionPills reactions={item.reactions} />
        </div>
        <div className="text-right whitespace-nowrap">
          <div className="text-[#D4AF37] font-display text-5xl tabular-nums">
            {item.total_reactions}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
            <Flame size={10} /> Total Love
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoenixCard({ item, rank }) {
  return (
    <div
      data-testid={`hop-card-${rank}`}
      className="bg-[#0F0F0F] border-2 border-zinc-900 p-4 hover:border-[#D4AF37] transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl">{item.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
              #{rank} · {item.subtitle}
            </div>
            <TrendingChip item={item} compact />
          </div>
          <div className="font-display text-lg text-white leading-tight mt-0.5">{item.title}</div>
          <AuthorLine author={item.author} small />
          <ReactionPills reactions={item.reactions} />
        </div>
        <div className="text-right whitespace-nowrap flex-shrink-0">
          <div className="text-[#D4AF37] font-bold tabular-nums text-xl">{item.total_reactions}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600">love</div>
        </div>
      </div>
    </div>
  );
}

function TrendingChip({ item, compact }) {
  if (!item?.trending) return null;
  const n = item.trending_count || 0;
  return (
    <span
      data-testid="hop-trending-chip"
      title={`+${n} reactions in the last hour`}
      className={
        "inline-flex items-center gap-1 bg-[#DC2626]/20 border border-[#DC2626] text-[#FCA5A5] font-bold uppercase tracking-widest " +
        (compact ? "text-[9px] px-1.5 py-[1px]" : "text-[10px] px-2 py-0.5")
      }
    >
      <span>🚀</span>
      <span>+{n}/hr</span>
    </span>
  );
}

function AuthorLine({ author, small }) {
  if (!author) return null;
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      {author.picture && (
        <img src={author.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
      )}
      <span className={small ? "text-xs text-neutral-400" : "text-sm text-neutral-300"}>
        by <span className="text-white font-bold">{author.name}</span>
        {author.handle && <span className="text-neutral-600"> · @{author.handle}</span>}
      </span>
    </span>
  );
  if (author.public && author.handle) {
    return (
      <Link to={`/u/${author.handle}`} className="hover:text-[#D4AF37] transition-colors block mt-1">
        {inner}
      </Link>
    );
  }
  return <div className="mt-1">{inner}</div>;
}

function ReactionPills({ reactions }) {
  const entries = Object.entries(reactions || {}).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex gap-1.5 mt-2 flex-wrap">
      {entries.map(([emoji, count]) => (
        <span
          key={emoji}
          className="inline-flex items-center gap-0.5 text-xs bg-zinc-900 px-1.5 py-0.5"
        >
          <span>{emoji}</span>
          <span className="tabular-nums text-[10px] text-neutral-400">{count}</span>
        </span>
      ))}
    </div>
  );
}
